import { useCallback, useEffect, useRef } from 'react';

/**
 * Kiosk idle watchdog.
 *
 * A guest who walks off must not leave their session on screen for the next
 * person — their face, their clip, and on the phone screen their number. Every
 * screen from the attract loop onward arms this; the attract screen itself
 * passes enabled=false.
 *
 * Listens on the window with capture, so a tap anywhere counts as presence
 * regardless of what swallows the event.
 */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'touchstart',
  'keydown',
  'wheel',
] as const;

export function useIdleTimeout({
  timeoutMs: rawTimeoutMs,
  enabled,
  onIdle,
}: {
  timeoutMs: number | null;
  enabled: boolean;
  onIdle: () => void;
}): { poke: () => void } {
  // A zero or negative timeout would reset the unit the instant a guest starts.
  // Treat it as "off" rather than trusting a misconfigured env var.
  const timeoutMs = rawTimeoutMs !== null && rawTimeoutMs > 0 ? rawTimeoutMs : null;
  const timerRef = useRef<number | null>(null);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const poke = useCallback(() => {
    if (!enabled || timeoutMs === null) return;
    clear();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      onIdleRef.current();
    }, timeoutMs);
  }, [clear, enabled, timeoutMs]);

  useEffect(() => {
    if (!enabled || timeoutMs === null) {
      clear();
      return;
    }

    poke();
    const handler = () => poke();
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, handler, { passive: true, capture: true }),
    );

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handler, { capture: true }));
      clear();
    };
  }, [clear, enabled, poke, timeoutMs]);

  return { poke };
}
